import React from 'react';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getReportComponent, determineReportType, reportComponentMap } from '../../config/reportTypeMapping';
import InputDataCard from './input/InputDataCard';

/**
 * ReportViewer Component
 * Reusable component for viewing report details with proper data parsing and component rendering
 * Based on ReportHistory.js renderViewContent logic
 *
 * @param {object} selectedReport - The report object from the list
 * @param {object} reportDetails - The detailed report data from API
 * @param {boolean} loading - Loading state
 */
const ReportViewer = ({ selectedReport, reportDetails, loading = false }) => {
    // Loading State
    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center flex-column p-5">
                <ProgressSpinner />
                <p className="text-600 mt-3">Loading report details...</p>
            </div>
        );
    }

    // No details state
    if (!reportDetails) {
        return <Message severity="error" text="No details available" />;
    }

    console.log('Report Details:', reportDetails);
    console.log('Selected Report:', selectedReport);
    console.log('Selected Report Name:', selectedReport?.report_name);
    console.log('Raw report_json:', reportDetails.report_json);

    // Parse report_json if it's a string
    let parsedReportData = null;
    try {
        parsedReportData = typeof reportDetails.report_json === "string"
            ? JSON.parse(reportDetails.report_json)
            : reportDetails.report_json;
        console.log('Parsed Report Data:', parsedReportData);
    } catch (error) {
        console.error('Error parsing report_json:', error);
        parsedReportData = reportDetails.report_json;
    }

    // If no parsed data, fall back to other sources
    if (!parsedReportData) {
        parsedReportData = reportDetails.api_response || reportDetails.data || reportDetails;
    }

    // Determine report type dynamically
    let reportType = selectedReport?.report_name;
    console.log('Initial Report Type from selectedReport:', reportType);

    // COMPATIBILITY: Handle both correct and incorrect backend responses
    // Check if this is actually a CA Membership report (regardless of what backend says)
    const hasMembershipNumber = selectedReport?.view_data?.status?.input?.membership_no ||
                               parsedReportData?.status?.input?.membership_no ||
                               reportDetails?.status?.input?.membership_no;

    const hasCaMembershipUrl = selectedReport?.download_url?.includes('/ca-membership/') ||
                               selectedReport?.download_url?.includes('/tartan/ca-membership/');

    const hasCaMembershipData = parsedReportData?.response?.AssociateYear ||
                                parsedReportData?.data?.response?.AssociateYear ||
                                selectedReport?.view_data?.AssociateYear;

    // If any CA Membership indicators are present, set the correct report type
    if (hasMembershipNumber || hasCaMembershipUrl || hasCaMembershipData) {
        if (reportType !== 'CA Membership' && reportType !== 'CA_MEMBERSHIP_VERIFICATION') {
            console.log(`Correcting report type from "${reportType}" to "CA Membership"`);
            console.log('Detection criteria:', {
                hasMembershipNumber,
                hasCaMembershipUrl,
                hasCaMembershipData
            });
        }
        reportType = 'CA Membership';
    }

    // Special logging for RC_DETAILS
    if (reportType === 'RC_DETAILS' || reportType === 'RC Details') {
        console.log('=== RC DETAILS REPORT DETECTED ===');
        console.log('Report Name:', reportType);
        console.log('Full Selected Report:', selectedReport);
        console.log('Report Details:', reportDetails);
        console.log('Parsed Report Data:', parsedReportData);
    }

    // Map database report names to component names
    const reportNameMapping = {
        'EMPLOYMENT_HISTORY': 'Employment History by Mobile',
        'UAN_EMPLOYMENT_HISTORY': 'Employment History by UAN',
        'BUREAU_REPORT': 'Equifax',
        'EXPERIAN_BUREAU_REPORT': 'Experian',
        'BANK_STATEMENT': 'Bank Statement Analysis',
        'PAN_VERIFICATION': 'PAN Details',
        'UAN_VERIFICATION': 'UAN',
        'VOTER_ID_VERIFICATION': 'Voter ID',
        'VOTER_ID_DETAILS': 'Voter ID',
        'DRIVING_LICENCE': 'Driving Licence',
        'DRIVING_LICENCE_VERIFICATION': 'Driving Licence',
        'DRIVING_LICENCE_V2': 'DL2',
        'DRIVING_LICENCE_VERIFICATION_V2': 'DL2',
        'DL_DETAILS': 'DL2',
        'CA_MEMBERSHIP_VERIFICATION': 'CA Membership',
        'MOBILE_BUREAU_LOOKUP': 'Mobile Number Lookup',
        'RC_DETAILS': 'RC Details'
    };

    // PRIORITY: Use the database report_name mapping first if available
    // This ensures BUREAU_REPORT -> Equifax and EXPERIAN_BUREAU_REPORT -> Experian
    if (reportNameMapping[reportType]) {
        const originalType = reportType;
        reportType = reportNameMapping[reportType];
        console.log('Mapped Report Type from DB name:', originalType, '->', reportType);

        // Special logging for RC_DETAILS mapping
        if (originalType === 'RC_DETAILS') {
            console.log('=== RC_DETAILS SUCCESSFULLY MAPPED TO RC Details ===');
        }
    } else if (!reportType) {
        // Only determine from data if we don't have a report_name
        reportType = determineReportType(parsedReportData);
        console.log('Determined Report Type from data:', reportType);

        // Additional check for PAN reports if still unknown
        if (reportType === 'Unknown' && parsedReportData) {
            if (parsedReportData.data?.response?.pan ||
                parsedReportData.response?.pan ||
                parsedReportData.data?.response?.panNumber ||
                parsedReportData.data?.response?.pancard_number ||
                (selectedReport?.report_name &&
                 (selectedReport.report_name.toUpperCase().includes('PAN') ||
                  selectedReport.report_name === 'PAN_VERIFICATION'))) {
                reportType = 'PAN Details';
                console.log('Detected PAN report from additional checks');
            }
        }
    }

    // Get initial component configuration - try both the mapped type and the original report name
    let componentConfig = getReportComponent(reportType);
    console.log('Getting component for report type:', reportType);
    console.log('Component config found:', componentConfig);

    // Special check for RC Details
    if (reportType === 'RC Details') {
        console.log('=== LOOKING FOR RC Details COMPONENT ===');
        console.log('Component exists?', !!componentConfig.component);
        if (!componentConfig.component) {
            console.log('RC Details component not found! Checking reportComponentMap...');
            // Let's check what's available in the map
            const availableTypes = Object.keys(reportComponentMap);
            console.log('Available report types in map:', availableTypes);
            console.log('Does map include "RC Details"?', availableTypes.includes('RC Details'));
            console.log('Does map include "RC_DETAILS"?', availableTypes.includes('RC_DETAILS'));
        }
    }

    // If not found with mapped type, try with original report name
    if (!componentConfig.component && selectedReport?.report_name) {
        console.log('Component not found with mapped type, trying original name:', selectedReport.report_name);
        componentConfig = getReportComponent(selectedReport.report_name);
        if (componentConfig.component) {
            console.log('Found component with original report name:', selectedReport.report_name);
            reportType = selectedReport.report_name;
        }
    }

    // Final fallback - check if it's actually Employment History by looking at the data
    if (reportType === 'Unknown' || !componentConfig.component) {
        // Check various possible structures for employment data
        const hasEmploymentData =
            parsedReportData?.response?.employment_data ||
            parsedReportData?.response?.response?.employment_data ||
            parsedReportData?.employment_data ||
            (typeof parsedReportData === 'object' &&
             JSON.stringify(parsedReportData).includes('employment_data'));

        if (hasEmploymentData || selectedReport?.report_name === 'Employment History by Mobile') {
            reportType = 'Employment History by Mobile';
            console.log('Forced to Employment History based on data structure');
            // Re-get component configuration with the corrected type
            componentConfig = getReportComponent(reportType);
        }
    }
    console.log('Final Report Type:', reportType);
    console.log('Component Config:', componentConfig);
    console.log('Has Component:', !!componentConfig.component);

    // Don't render if no parsed data
    if (!parsedReportData) {
        return <Message severity="warn" text="No report data available to display" />;
    }

    if (!componentConfig.component) {
        return (
            <>
                <Message severity="info" text={`Report component not configured for type: ${reportType}`} />
                <div className="mt-3">
                    <pre className="p-3 surface-100 border-round" style={{ maxHeight: '400px', overflow: 'auto' }}>
                        {JSON.stringify(parsedReportData, null, 2)}
                    </pre>
                </div>
            </>
        );
    }

    // Get component and create enhanced props with parsed data
    const Component = componentConfig.component;

    // Create enhanced reportDetails object with parsed data
    const enhancedReportDetails = {
        ...reportDetails,
        report_json: parsedReportData,
        api_response: parsedReportData,
        data: parsedReportData
    };

    const componentProps = componentConfig.props(enhancedReportDetails, selectedReport);

    // Override reportData prop to pass the complete report structure with parsed JSON
    // BUT keep the PDF-related props that might have been set by the mapping
    // ALWAYS check the root reportDetails for PDF fields
    const pdfDownloadUrl = reportDetails?.pdf_download_url || componentProps.pdfDownloadUrl || reportDetails?.pdf_url;
    const pdfPasswordHint = reportDetails?.pdf_password_hint || componentProps.pdfPasswordHint;

    componentProps.reportData = enhancedReportDetails;

    // Re-add PDF props after overriding reportData - ALWAYS pass them
    componentProps.pdfDownloadUrl = pdfDownloadUrl || reportDetails?.pdf_download_url || reportDetails?.pdf_url;
    componentProps.pdfPasswordHint = pdfPasswordHint || reportDetails?.pdf_password_hint;

    console.log('Component Props:', componentProps);
    console.log('PDF URL passed to component:', componentProps.pdfDownloadUrl);
    console.log('PDF Hint passed to component:', componentProps.pdfPasswordHint);
    console.log('Report Details Structure:', {
        'reportDetails.pdf_download_url': reportDetails?.pdf_download_url,
        'reportDetails.pdf_url': reportDetails?.pdf_url,
        'reportDetails.id': reportDetails?.id,
        'Full reportDetails': reportDetails
    });

    // Conditionally render the component only if we have parsed data
    return parsedReportData && (
        <div className="report-viewer">
            {/* Input Data Card */}
            <InputDataCard
                inputData={selectedReport?.input_data}
                inputSummary={selectedReport?.input_summary}
                reportType={reportType}
                submittedAt={selectedReport?.input_submitted_at}
                defaultExpanded={true}
                showExpandButton={true}
            />

            {/* Report Output Component */}
            <Component {...componentProps} />
        </div>
    );
};

export default ReportViewer;
